import type { ThemeState } from '@atlaskit/tokens';

import type { ExperienceTrackerAPI } from '@confluence/experience-tracker';
import { WHITEBOARD_CONTENT_EXPERIENCE } from '@confluence/experience-tracker';
import type { WhiteboardFeatures as features } from '@confluence/whiteboard-utils';
import type {
	ContentStatus as BoardStatus,
	MainActions,
	Collaborators,
	MoreActions,
} from '@confluence/content-types-header';
import type { ContentTypeContainerService } from '@confluence/content-types-bridge/entry-points/content-types-container-service';
import type { ContentTypeRouteService } from '@confluence/content-types-bridge/entry-points/content-types-route-service';
import type { ContentFeedbackService } from '@confluence/content-types-bridge/entry-points/content-types-feedback-service';
import {
	contentMainActionsService,
	contentCollaboratorsService,
	contentBoardStatusService,
	contentMoreActionsService,
} from '@confluence/content-types-bridge';
import { FeedbackSource } from '@confluence/global-feedback-collector';
import { ContentType } from '@confluence/content-types-utils';

import type { WhiteboardTokenService } from './services/whiteboard-token-service';
import type {
	OnboardingStartHandler,
	OnboardingEndHandler,
	WhiteboardOnboardingService,
} from './services/whiteboard-onboarding-service';
import type { WhiteboardOrgDataService } from './services/whiteboard-org-data-service';
import { WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT } from './perf.config';
import type { WhiteboardNavigationService } from './services/whiteboard-navigation-service';
import type { WhiteboardBridgeAPI } from './whiteboard-bridge-api-type';
import type { WhiteboardEditionUpgradeService } from './services/whiteboard-edition-upgrade-service';
import type { WhiteboardThemingService } from './services/whiteboard-theming-service';
import type {
	WhiteboardResolvedCommentsService,
	ResolvedCommentHandler,
} from './services/whiteboard-resolved-comment-service';
import type { WhiteboardInternalModalService } from './services/whiteboard-internal-modal-service';
import type { integrationService } from './useIntegrationService';
import { ContentTypesFlagService } from '@confluence/content-types-bridge/entry-points/content-types-flag-service';

interface WhiteboardBridgeAPIContext {
	sessionData: {
		accountId: string | null;
		tenantId: string;
	};
}

interface WhiteboardBridgeAPIServices {
	containerService: ContentTypeContainerService;
	experienceTracker: ExperienceTrackerAPI;
	isFeatureActive: (featureName: features) => boolean;
	navigationService: WhiteboardNavigationService;
	onboardingService: WhiteboardOnboardingService;
	orgDataService: WhiteboardOrgDataService;
	routeService: ContentTypeRouteService;
	tokenService: WhiteboardTokenService;
	editionUpgradeService: WhiteboardEditionUpgradeService;
	themingService: WhiteboardThemingService;
	feedbackService: ContentFeedbackService;
	onThemeLoaded: () => void;
	resolvedCommentService: WhiteboardResolvedCommentsService;
	whiteboardInternalModalService: WhiteboardInternalModalService;
	flagService: ContentTypesFlagService;
	integrationService: integrationService;
	platformFeatureFlagResolver: (flagKey: string) => boolean;
}

export class WhiteboardBridgeAPIBuilder {
	private context: WhiteboardBridgeAPIContext;
	private services: WhiteboardBridgeAPIServices;

	private constructor(ctx: WhiteboardBridgeAPIContext, services: WhiteboardBridgeAPIServices) {
		this.context = ctx;
		this.services = services;
	}

	static with(ctx: WhiteboardBridgeAPIContext, services: WhiteboardBridgeAPIServices) {
		return new WhiteboardBridgeAPIBuilder(ctx, services);
	}

	build(): WhiteboardBridgeAPI {
		return {
			goToContent: (id, query) => {
				this.services.tokenService.resetToken();
				this.services.navigationService.goToContent(id, query);
			},
			goToContentCreate: () => {
				this.services.tokenService.resetToken();
				this.services.navigationService.goToContentCreate();
			},
			goToHome: () => {
				this.services.tokenService.resetToken();
				this.services.navigationService.goToHome();
			},
			goToContainer: (containerKey) => {
				this.services.tokenService.resetToken();
				this.services.navigationService.goToContainer(containerKey);
			},
			onTokenInit: (cb) => {
				this.services.tokenService.onTokenInit(cb);
			},
			onTokenChange: (cb) => {
				this.services.tokenService.onTokenChange(cb);
			},
			preloadToken: (id?: string) => {
				this.services.tokenService.preloadToken(id).catch((_err) => {});
			},
			getToken: (id?: string) => {
				return this.services.tokenService.getToken(id);
			},
			refreshToken: (id?: string) => {
				return this.services.tokenService.refreshToken(id).catch((_err) => {});
			},
			onContainerInit: (cb) => {
				this.services.containerService.onContainerInit(cb);
			},
			onContainerChange: (cb) => {
				this.services.containerService.onContainerChange(cb);
			},
			onRouteChange: (cb) => {
				this.services.routeService.onRouteChange(cb);
			},
			updateCollaborators: (collaborators: Collaborators) => {
				// TODO - Move this service away from singleton pattern
				contentCollaboratorsService.updateActions(collaborators);
			},
			updateMainActions: (actions: MainActions) => {
				// TODO - Move this service away from singleton pattern
				contentMainActionsService.updateActions(actions);
			},
			updateBoardStatus: (status: BoardStatus) => {
				// TODO - Move this service away from singleton pattern
				contentBoardStatusService.updateActions(status);
			},
			updateMoreActions: (items: MoreActions) => {
				// TODO - Move this service away from singleton pattern
				contentMoreActionsService.updateActions(items);
			},
			onContentLoadSucceeded: (attributes) => {
				const { experienceTracker } = this.services;

				experienceTracker.succeed({
					name: WHITEBOARD_CONTENT_EXPERIENCE,
					attributes,
				});

				WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT.stop({
					// TODO - look into this type issue
					// @ts-ignore
					customData: attributes,
				});
			},
			onContentLoadFailed: (error, attributes) => {
				const { experienceTracker } = this.services;

				error.message = `fabric error: ${error.message}`;

				experienceTracker.stopOnError({
					name: WHITEBOARD_CONTENT_EXPERIENCE,
					attributes,
					error,
				});

				WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT.cancel();
			},
			onContentLoadAborted: (abortReason, attributes) => {
				const { experienceTracker } = this.services;

				experienceTracker.abort({
					name: WHITEBOARD_CONTENT_EXPERIENCE,
					reason: abortReason || 'unknown from whiteboard bridge',
					attributes,
				});

				WHITEBOARD_VIEW_PAGE_CONTENT_SEGMENT.cancel();
			},
			isFeatureActive: (...args) => this.services.isFeatureActive(...args),
			platformFeatureFlagResolver: (flagKey) => this.services.platformFeatureFlagResolver(flagKey),
			registerStartOnboardingCallback: (cb: OnboardingStartHandler) => {
				this.services.onboardingService.registerStartOnboardingCallback(cb);
			},
			registerOnOnboardingEndCallback: (cb: OnboardingEndHandler) => {
				this.services.onboardingService.registerOnOnboardingEndCallback(cb);
			},
			startOnboarding: (initialSpotlightScreen?: string) => {
				this.services.onboardingService.startOnboarding(initialSpotlightScreen);
			},
			onOnboardingReady: (eligibleScreens: string[]) => {
				const { onboardingService } = this.services;
				const {
					hasStartedWhiteboardOnboardingSpotlight: isEligibleForBetaOnboarding,
					isEligibleForGAOnboarding,
					isNewWhiteboard,
				} = onboardingService;

				if (eligibleScreens.includes('spotlight-templates')) {
					if (!isEligibleForGAOnboarding && isEligibleForBetaOnboarding) {
						onboardingService.startOnboarding();
					}
				}

				if (eligibleScreens.includes('spotlight-sticky-viewer')) {
					if (isEligibleForGAOnboarding && !isNewWhiteboard) {
						onboardingService.startOnboarding('spotlight-sticky-viewer');
					}
				}

				if (eligibleScreens.includes('spotlight-sticky-creator')) {
					if (isEligibleForGAOnboarding && isNewWhiteboard) {
						onboardingService.showTitleSpotlight();
					}
				}

				if (eligibleScreens.includes('spotlight-exports')) {
					onboardingService.showHeaderSpotlight();
				}

				if (eligibleScreens.includes('spotlight-follow-cursor')) {
					onboardingService.showCollaboratorSpotlight();
				}
			},
			onOnboardingChange: (screen: string) => {
				this.services.onboardingService.onOnboardingChange(screen);
			},
			onOnboardingEnd: (screen: string) => {
				this.services.onboardingService.onOnboardingEnd(screen);
			},
			getOnboardingSkipped: () => {
				return this.services.onboardingService.onboardingSkipped;
			},
			setOnboardingSkipped: (skipped: boolean) => {
				this.services.onboardingService.onboardingSkipped = skipped;
			},
			onOrgIdChange: (cb) => {
				this.services.orgDataService.onOrgIdChange(cb);
			},
			getOrgId: () => {
				return this.services.orgDataService.getId();
			},
			showEditionUpgradeCTA: (props) => {
				return this.services.editionUpgradeService.showUpgradeExperience(props);
			},
			closeEditionUpgradeCTA: () => {
				this.services.editionUpgradeService.closeUpgradeExperience();
			},
			sessionData: this.context.sessionData,
			onThemeChange: (cb) => {
				this.services.themingService.onThemeChange(cb);
			},
			getCurrentTheme: (): Partial<ThemeState> | undefined => {
				return this.services.themingService.getTheme();
			},
			showFeedbackCollectorModal: (props) => {
				return this.services.feedbackService.showFeedbackCollector({
					...props,
					feedbackSource: FeedbackSource.WHITEBOARD,
					contentType: ContentType.WHITEBOARD,
					showTypeField: true,
				});
			},
			closeFeedbackCollectorModal: () => {
				this.services.feedbackService.closeFeedbackModal();
			},
			onThemeLoaded: this.services.onThemeLoaded,
			registerOnReopenResolvedComment: (cb: ResolvedCommentHandler) => {
				this.services.resolvedCommentService.registerOnReopenResolvedComment(cb);
			},
			showWhiteboardInternalModal: (props) => {
				return this.services.whiteboardInternalModalService.showModal(props);
			},
			closeWhiteboardInternalModal: () => {
				this.services.whiteboardInternalModalService.closeModal();
			},
			showFlag: (props) => {
				this.services.flagService.showFlag(props);
			},
			hideFlag: (flagId) => {
				this.services.flagService.hideFlag(flagId);
			},
			triggerIntegration: (...props) =>
				this.services.integrationService.triggerIntegration(...props),
			checkIntegration: (...props) => this.services.integrationService.checkIntegration(...props),
		};
	}
}
